<template>
    <div>
        <Component
            :is="globalStore.header.component"
            v-if="globalStore && globalStore.header"
            :global="globalStore.global.content"
            :blok="globalStore.header"
        />
        <main>
            <Component
                :is="story.content.component"
                v-if="story"
                :blok="story.content"
                :products="products"
                :airport-stats="products.airportStats"
            />
        </main>
        <Component
            :is="globalStore.footer.component"
            v-if="globalStore && globalStore.footer"
            :global="globalStore.global.content"
            :blok="globalStore.footer"
        />
    </div>
</template>

<script setup lang="ts">
import { useGlobalStore } from '@nuxt-monorepo/base/app/store/global';
import type { IAirportProducts } from '@nuxt-monorepo/ui/types';
import { getProductsFromResponse } from '~/../app/services/apiProductService';
const globalStore: any = useGlobalStore();
const { locale } = useI18n();
const route: any = useRoute();

// Constant variable for access to Runtime Config
const { STORYBLOK_FOLDER, STORYBLOK_VERSION, STORYBLOK_ROUTE_BLOCKED }: any = useRuntimeConfig().public;

// Need to resolve shared components to allow the storyblok API to expose this for us to use.
const resolveRelations: string[] = [
    'CoreSharedComponent.SharedComponent',
    'PSFAirportPage.globals',
    'PSFAirportPage.AirportEntity',
];

const sbLocation = `/${STORYBLOK_FOLDER}/pages/airports/hotel-parking/${route.params.IATA}`;

const isInVisualEditor = !!route?.query?.['_storyblok_tk[token]']; //Check if frontend is opened in visual editor
// Perform request to Storyblok to retrieve content
const data: any = await useAsyncStoryblok(sbLocation, {
    // Enable deep reactivity to fix live editing when in visual editor.
    // Because of changes for useAsyncData in Nuxt4 https://github.com/storyblok/monoblok/issues/210#issuecomment-3172675601
    deep: isInVisualEditor,
    api: {
        version: STORYBLOK_VERSION === 'published' ? 'published' : 'draft',
        language: locale.value,
        resolve_relations: resolveRelations,
    },
    bridge: {
        resolveRelations: resolveRelations,
    },
});

const story = data?.story;
const error = data?.error;

if (story) {
    let config: { STORYBLOK_VERSION: string; STORYBLOK_FOLDER: string } = {
        STORYBLOK_VERSION: STORYBLOK_VERSION,
        STORYBLOK_FOLDER: STORYBLOK_FOLDER,
    };
    setSeoMeta(story.value?.content?.seo, config, route);
    if (story.value?.content?.globals) {
        globalStore.update(story.value.content.globals);
    }
}

function getStoryblokBlockedRoutes() {
    if (STORYBLOK_ROUTE_BLOCKED) {
        const blocked = STORYBLOK_ROUTE_BLOCKED.split(', ');
        return blocked.some((block) => route.path.includes(block));
    } else {
        return false;
    }
}

// Send user to error page if no story found
if (getStoryblokBlockedRoutes()) {
    throw createError({
        statusCode: 404,
        statusMessage: 'Page Not Found',
        fatal: true,
    });
}

if (error?.value) {
    throw createError({
        statusCode: error.value.statusCode ?? 500,
        statusMessage: error.value.statusMessage ?? 'Failed to load page',
        fatal: true,
    });
}

onMounted(() => {
    // Allow use of Storyblok Bridge (Visual Editor)
    useStoryblokBridge(story.id, (evStory: any) => (story.value = evStory), {
        resolveRelations,
    });
});

const airport: any = story.value.content.AirportEntity.content;
const apiEntity: any = (story.value.content.api_entity || [])[0];
const slug = apiEntity?.og_slug ?? `${airport.code?.toLowerCase()}/airport-hotels-with-parking`;
const slugUrl = apiEntity?.og_slug_url ?? `${airport.code?.toLowerCase()}/airport-hotels-with-parking`;
const slugHotelsOnly = apiEntity?.og_slug ? apiEntity?.og_slug : 'airport-hotels';
const psfHotels: any = await useAsyncData(
    `${airport.code?.toLowerCase()}/airport-hotels`,
    async () =>
        await $privateApi('airport-parking/content', {
            method: 'POST',
            body: {
                domain: 'psf.com',
                slug: slugHotelsOnly,
                airportCode: airport.code?.toLowerCase(),
            },
        }),
    {
        lazy: true,
    }
);

const psfHotelsParking: any = await useAsyncData(
    `${airport.code?.toLowerCase()}/airport-hotels-with-parking`,
    async () =>
        await $privateApi('contents/pages', {
            method: 'POST',
            params: {
                domain: 'psf.com',
                slug: slug,
                full_url: `https://parksleepfly.com/${slugUrl}`,
            },
        }),
    {
        lazy: true,
    }
);

// As all API call results are reactive need to manualy replace needed values.
// Can't assign psfHotelsParking as main result because it will be overwritten by psfHotels
const psf = psfHotels;
psf.data.value.data.hotelsWithParking = psfHotelsParking.data.value.data.hotels;
psf.data.value.data.parking = psfHotelsParking.data.value.data.parking;
psf.data.value.data.airport = psfHotelsParking.data.value.data.airport;
psf.data.value.data.airport_stats = psfHotelsParking.data.value.data.airport_stats;
psf.data.value.data.content = psfHotelsParking.data.value.data.content;
psf.data.value.data.parking = psfHotelsParking.data.value.data.parking;

const products: IAirportProducts = getProductsFromResponse(psf);

provide('productsList', products);
provide('airportData', airport);
</script>
